import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { AlertBadge } from '@/components/alert/AlertBadge'
import { AlertCard } from '@/components/alert/AlertCard'
import { useAlerts, useTriggeredAlerts } from '@/hooks/useAlerts'
import { useAuthStore } from '@/stores/authStore'
import { Plus, AlertTriangle, History } from 'lucide-react'
import type { Device } from '@/types/device.types'

interface DeviceAlertsProps {
  device: Device
}

export const DeviceAlerts = ({ device }: DeviceAlertsProps) => {
  const [historyTab, setHistoryTab] = useState<'active' | 'dismissed'>('active')
  const user = useAuthStore((state) => state.user)
  const linkedAccounts = useAuthStore((state) => state.linkedAccounts)

  // Check if user is admin for this device's account
  const accountLink = linkedAccounts.find(acc => acc.id === device.accountId)
  const isAdmin = user?.isSuperUser || accountLink?.isAdmin || false

  // Fetch all alerts and filter for this device
  const { data: alertsData, isLoading: alertsLoading, error: alertsError } = useAlerts()

  // Fetch triggered alerts
  const { data: activeAlertsData, isLoading: activeLoading, error: activeError } = useTriggeredAlerts(false)
  const { data: dismissedAlertsData, isLoading: dismissedLoading, error: dismissedError } = useTriggeredAlerts(true)

  // Filter alerts that apply to this device
  // Note: We can't perfectly filter without knowing which devices are assigned to each alert
  // So we show all alerts that match the device type or are global
  const deviceAlerts = alertsData?.alerts.filter(alert => {
    if (!alert.deviceType) return true // Global alerts
    return alert.deviceType.toLowerCase() === device.deviceType?.toLowerCase()
  }) || []

  // Filter triggered alerts for this device
  const activeAlerts = activeAlertsData?.alerts.filter(alert => alert.deviceId === device.id) || []
  const dismissedAlerts = dismissedAlertsData?.alerts.filter(alert => alert.deviceId === device.id) || []

  if (alertsError || activeError || dismissedError) {
    return <ErrorMessage message="Failed to load alerts. Please try again." />
  }

  return (
    <div className="space-y-6">
      {/* Alert Configurations */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Alert Configurations</CardTitle>
          </div>
          {isAdmin && (
            <Button size="sm" asChild>
              <Link to={`/alerts/new?deviceId=${device.id}&deviceType=${device.deviceType}`}>
                <Plus className="mr-2 h-4 w-4" />
                New Alert
              </Link>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {alertsLoading ? (
            <div className="py-8">
              <LoadingSpinner />
            </div>
          ) : deviceAlerts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {deviceAlerts.map((alert) => (
                  <TableRow key={alert.id}>
                    <TableCell className="font-medium">{alert.alertName}</TableCell>
                    <TableCell className="text-sm">
                      {alert.paramLabel || alert.measurementColumn} {alert.comparisonOperator}{' '}
                      {alert.thresholdValue} {alert.unit}
                    </TableCell>
                    <TableCell>
                      <AlertBadge severity={alert.severityLevel} />
                    </TableCell>
                    <TableCell>{alert.recipientCount}</TableCell>
                    <TableCell>
                      {alert.isActive ? (
                        <span className="text-green-600 text-sm">Active</span>
                      ) : (
                        <span className="text-gray-400 text-sm">Inactive</span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/alerts/${alert.id}/edit`}>Edit</Link>
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <p>No alerts configured for this device type</p>
              {isAdmin && (
                <Button className="mt-4" variant="outline" asChild>
                  <Link to={`/alerts/new?deviceId=${device.id}&deviceType=${device.deviceType}`}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create First Alert
                  </Link>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alert History */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Alert History</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={historyTab} onValueChange={(v) => setHistoryTab(v as 'active' | 'dismissed')}>
            <TabsList className="mb-4">
              <TabsTrigger value="active">
                Active Alerts
                {activeAlerts.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                    {activeAlerts.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="dismissed">Dismissed Alerts</TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-4">
              {activeLoading ? (
                <LoadingSpinner />
              ) : activeAlerts.length > 0 ? (
                activeAlerts.map((alert) => <AlertCard key={alert.id} alert={alert} />)
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No active alerts for this device
                </div>
              )}
            </TabsContent>

            <TabsContent value="dismissed" className="space-y-4">
              {dismissedLoading ? (
                <LoadingSpinner />
              ) : dismissedAlerts.length > 0 ? (
                dismissedAlerts.map((alert) => <AlertCard key={alert.id} alert={alert} />)
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No dismissed alerts for this device
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
